/**
 * The Roadmap's dated view (D193 phase 2B), DOM-free so it is tested
 * (test/roadmapGanttModel.test.ts):
 *
 *   - the Timeline layout's switch: "What happened" (the runs that served
 *     the roadmap, the work timeline) and "What’s ahead" (the dated Gantt:
 *     real work, targets, a forecast from the recent pace);
 *   - the Target picker: an optional month or quarter, chosen from a list
 *     rather than typed, with the words a row shows quietly;
 *   - the grounding the view and its Picture menu share, so what is saved or
 *     exported is the picture on the screen.
 */
import { type GanttGrounding, groundRoadmapGantt, pointsAWeek, type RoadmapRowLike, type RoadmapRunLike } from './roadmapGantt.ts';
import { monthName, monthYear } from '@aioe/shared/wording/dateTime';
import { normaliseRoadmapTarget, roadmapTargetEnd, roadmapTargetWords } from '@aioe/shared/roadmap/model';

// ── The switch ───────────────────────────────────────────────────────

export type TimelineMode = 'happened' | 'ahead';

export const TIMELINE_MODES: ReadonlyArray<{ id: TimelineMode; label: string; hint: string }> = [
  { id: 'happened', label: 'What happened', hint: 'The runs that served each item' },
  { id: 'ahead', label: 'What’s ahead', hint: 'Targets, and a forecast where there is none' },
];

/** The mode a viewer last chose, or the dated view. */
export function readTimelineMode(stored: string | null | undefined): TimelineMode {
  return stored === 'happened' ? 'happened' : 'ahead';
}

export type GanttLanes = 'series' | 'phase' | 'area';

export const GANTT_LANES: ReadonlyArray<{ id: GanttLanes; label: string }> = [
  { id: 'series', label: 'Lanes by theme' },
  { id: 'phase', label: 'Lanes by phase' },
  { id: 'area', label: 'Lanes by area' },
];

export function readGanttLanes(stored: string | null | undefined): GanttLanes {
  return stored === 'phase' || stored === 'area' ? stored : 'series';
}

const DAY = 86_400_000;

/** The runs the Gantt reads: the last 90 days (a host keeps that much, and the pace looks back 12 weeks), to now. */
export function ganttRunsRange(now: number): { from: number; to: number } {
  // Whole minutes, so a re-render does not ask again for a range a millisecond later.
  const to = Math.ceil(now / 60_000) * 60_000;
  return { from: to - 90 * DAY, to };
}

/** The dated picture for the view and its Picture menu: the same call, so both show the same thing. */
export function groundForView(rows: readonly RoadmapRowLike[], runs: readonly RoadmapRunLike[], opts: { now: number; level: 'repository' | 'project'; lanes: GanttLanes }): GanttGrounding | undefined {
  if (!rows.length) return undefined;
  const today = new Date(opts.now).toISOString().slice(0, 10);
  return groundRoadmapGantt(rows, { today, runs, level: opts.level, laneBy: opts.lanes, scope: opts.level });
}

/**
 * The quiet line above the picture: what the forecast went by (or why there
 * is none) in one sentence, and the targets the pace says will be missed, by
 * id — the whole explanation is the line's tooltip and the picture's notes.
 */
export function forecastLine(g: GanttGrounding | undefined): { summary: string; late?: string; full: string } | undefined {
  if (!g) return undefined;
  const f = g.forecast;
  if (!f.pace) return { summary: f.explanation.replace(/ Items without a target are listed under "No date yet"\./, ' What has no target has no date yet.'), full: f.explanation };
  const weeks = f.pace.windowDays % 7 === 0 ? `${f.pace.windowDays / 7} weeks` : `${f.pace.windowDays} days`;
  // Which basis the picture used, in its own words: estimated points when most items have one, else items.
  const pace =
    f.pace.basis === 'points' && f.pace.pointsPerWeek !== undefined
      ? `about ${pointsAWeek(f.pace.pointsPerWeek)} (${f.pace.points ?? 0} points of estimated work finished)`
      : `about one item every ${f.pace.daysPerItem} day${f.pace.daysPerItem === 1 ? '' : 's'} (${f.pace.finished} finished)`;
  const summary = `Forecast at the pace of the last ${weeks}: ${pace}. A forecast is not a promise.`;
  const late = [...f.late.keys()];
  return { summary, ...(late.length ? { late: `${late.length === 1 ? 'One target looks' : `${late.length} targets look`} late at that pace: ${late.join(', ')}.` } : {}), full: f.explanation };
}

// ── The Target picker ────────────────────────────────────────────────

export type TargetKind = 'none' | 'month' | 'quarter' | 'day';

export function targetKind(target: string | undefined): TargetKind {
  if (!target) return 'none';
  if (/^\d{4}-Q[1-4]$/.test(target)) return 'quarter';
  if (/^\d{4}-\d{2}$/.test(target)) return 'month';
  return 'day';
}

export interface TargetOption {
  id: string;
  label: string;
  /** "Oct–Dec" for a quarter; nothing for a month. */
  detail?: string;
}


/**
 * What the picker offers: the next 18 months, or the next 6 quarters, from
 * this month on — and the row's own target first when it is earlier or is a
 * day, so an existing value is never lost by opening the picker.
 */
export function targetOptions(kind: 'month' | 'quarter', now: number, current?: string): TargetOption[] {
  const d = new Date(now);
  const y = d.getUTCFullYear();
  const m = d.getUTCMonth();
  const out: TargetOption[] = [];
  if (kind === 'month') {
    for (let i = 0; i < 18; i++) {
      const yy = y + Math.floor((m + i) / 12);
      const mm = (m + i) % 12;
      out.push({ id: `${yy}-${String(mm + 1).padStart(2, '0')}`, label: monthYear(Date.UTC(yy, mm, 1), { width: 'short', timeZone: 'UTC' }) });
    }
  } else {
    const q0 = Math.floor(m / 3);
    for (let i = 0; i < 6; i++) {
      const yy = y + Math.floor((q0 + i) / 4);
      const q = (q0 + i) % 4;
      out.push({ id: `${yy}-Q${q + 1}`, label: `Q${q + 1} ${yy}`, detail: `${monthName(Date.UTC(yy, q * 3, 1), 'short', { timeZone: 'UTC' })}–${monthName(Date.UTC(yy, q * 3 + 2, 1), 'short', { timeZone: 'UTC' })}` });
    }
  }
  if (current && !out.some((o) => o.id === current)) out.unshift({ id: current, label: roadmapTargetWords(current), detail: isPastTarget(current, now) ? 'past' : undefined });
  return out;
}

/** True when a target's last day is before today. */
export function isPastTarget(target: string, now: number): boolean {
  const end = roadmapTargetEnd(target);
  return !!end && end < new Date(now).toISOString().slice(0, 10);
}

/** What a row shows, quietly: "Target Nov 2026", or "Was due Sep 2026" for an open row past it; nothing without one. */
export function targetBadge(row: { target?: string; status: string }, now: number): { text: string; overdue: boolean } | undefined {
  const target = normaliseRoadmapTarget(row.target);
  if (!target) return undefined;
  const overdue = row.status !== 'done' && row.status !== 'wont-do' && row.status !== 'deferred' && isPastTarget(target, now);
  return { text: overdue ? `Was due ${roadmapTargetWords(target)}` : `Target ${roadmapTargetWords(target)}`, overdue };
}

/** The kind a picker opens on for a value, and the default the kind switch picks. */
export function defaultTargetFor(kind: 'month' | 'quarter', now: number): string {
  return targetOptions(kind, now)[kind === 'month' ? 1 : 0].id;
}

/** The picker's sentence under the field: what a target is, and what it is not. */
export const TARGET_HINT = 'When it should be done. A person’s date: the dated roadmap shows it as set, and forecasts only items without one.';
